import { createContext, useState, useContext, useEffect } from "react";
import { useRef } from "react";
import { authFetch } from "../utils/authFetch";
import API_ENDPOINTS from "../apiConfig";
import { fetchPlatesForAreas } from "../hooks/plateService";

const SiteContext = createContext();

export const SiteProvider = ({ children }) => {
  // ---------------- IDs (source of truth) ----------------
  const [siteId, setsiteId] = useState(() => {
    const stored = localStorage.getItem("siteId");
    return stored ? Number(stored) : null;
  });

  const [areaId, setareaId] = useState(() => {
    const stored = localStorage.getItem("areaId");
    return stored ? Number(stored) : null;
  });

  // ---------------- Data ----------------
  const [sites, setSites] = useState([]);
  const [areas, setAreas] = useState([]);
  const [platesByArea, setPlatesByArea] = useState({});
  const [loadingSites, setLoadingSites] = useState(true);
  // ---------------- Derived objects ----------------
  const selectedSite =
    sites.find((s) => s.id === siteId) || null;

  const selectedArea =
    areas.find((a) => a.id === areaId) || null;

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setareaId(null);
  }, [siteId]);

  // ---------------- Persist IDs ----------------
  useEffect(() => {
    if (siteId !== null) {
      localStorage.setItem("siteId", siteId);
    } else {
      localStorage.removeItem("siteId");
    }
  }, [siteId]);

  useEffect(() => {
    if (areaId !== null) {
      localStorage.setItem("areaId", areaId);
    } else {
      localStorage.removeItem("areaId");
    }
  }, [areaId]);

  const fetchSites = async () => {
    if (!localStorage.getItem("token")) return;
    setLoadingSites(true);
    try {
      const res = await authFetch(API_ENDPOINTS.getsites);
      if (!res.ok) throw new Error("Failed to fetch sites");
      const data = await res.json();
      setSites(data);

      const storedId = Number(localStorage.getItem("siteId"));
      const match = storedId && data.find(s => s.id === storedId);
      if (match) {
        localStorage.setItem("actualUserRole", match.user_role);
        localStorage.setItem("actualUserScope", match.user_scope || "");
      } else if (data.length > 0) {
        setsiteId(data[0].id);
        localStorage.setItem("siteId", data[0].id);
        localStorage.setItem("actualUserRole", data[0].user_role);
        localStorage.setItem("actualUserScope", data[0].user_scope || "");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSites(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    const fetchAreas = async () => {
      if (siteId == null) return;

      try {
        const res = await authFetch(`${API_ENDPOINTS.getsites}/${siteId}/parking-areas`);

        if (!res.ok) throw new Error("Failed to fetch areas");

        const data = await res.json();
        setAreas(data);

        const platesData = await fetchPlatesForAreas(data);
        setPlatesByArea(platesData);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAreas();
  }, [siteId]);

  const resetContext = () => {
    setsiteId(null);
    setareaId(null);
    setSites([]);
    setAreas([]);
    setPlatesByArea({});
    fetchSites();
  };

  return (
    <SiteContext.Provider
      value={{
        // IDs
        siteId,
        setsiteId,
        areaId,
        setareaId,

        // Derived objects
        selectedSite,
        selectedArea,

        // Data
        sites,
        setSites,
        loadingSites,
        setLoadingSites,
        areas,
        setAreas,
        platesByArea,
        setPlatesByArea,

        resetContext,
        fetchSites,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => useContext(SiteContext);